import API from "../constants/API";
import { authHeader } from "../common/mainfunction";
import { JSONParser } from "../common/function";
import {
  API_V1_BASE,
  GET_PERMISSION_CATALOG_URL,
  GET_ROLES_URL,
  GET_USER_PERMISSIONS_URL,
  UPDATE_USER_PERMISSIONS_URL,
  UPDATE_USER_ROLE_URL,
  GET_PERMISSION_SETS_URL,
  APPLY_PERMISSION_SET_URL,
} from "../common/url";
import type {
  ApiResponse,
  PermissionCatalogEntry,
  Role,
  Permission,
  PermissionData,
  PermissionSet,
} from "../types/api";

/**
 * Get all available permissions from catalog
 */
export const getPermissionCatalog = async (): Promise<ApiResponse<PermissionCatalogEntry[]>> => {
  const config = {
    method: "GET",
    url: GET_PERMISSION_CATALOG_URL,
    headers: authHeader(),
  };

  try {
    const response = await API(config);
    const parsed = JSONParser(response.data, { status: "Error", data: [] });
    // Enterprise /admin/permissions -> [{code, description}]. Adapt to the FE catalog shape.
    const data = Array.isArray(parsed.data)
      ? parsed.data.map((x: { code: string; description?: string | null }) => ({
          permissionId: x.code,
          permissionKey: x.code,
          permissionName: x.description || x.code,
          description: x.description ?? "",
          category: "",
        }))
      : [];
    return { ...parsed, data } as unknown as ApiResponse<PermissionCatalogEntry[]>;
  } catch (error) {
    console.error("Failed to get permission catalog:", error);
    throw error;
  }
};

/**
 * Get all roles
 */
export const getRoles = async (): Promise<ApiResponse<Role[]>> => {
  const config = {
    method: "GET",
    url: GET_ROLES_URL,
    headers: authHeader(),
  };

  try {
    const response = await API(config);
    const parsed = JSONParser(response.data, { status: "Error", data: [] });
    // Enterprise /admin/roles -> [{roleId(ULID), roleCode, description, permissions[]}]. roleName = roleCode.
    const data = Array.isArray(parsed.data)
      ? parsed.data.map(
          (x: {
            roleId: string;
            roleCode: string;
            description?: string | null;
            permissions?: string[];
          }) => ({
            roleId: x.roleId,
            roleName: x.roleCode,
            description: x.description ?? "",
            // permissions kept so callers can classify a role (e.g. admin = grants ADMIN_MANAGE_USERS).
            permissions: x.permissions ?? [],
          })
        )
      : [];
    return { ...parsed, data } as unknown as ApiResponse<Role[]>;
  } catch (error) {
    console.error("Failed to get roles:", error);
    throw error;
  }
};

/**
 * Get user's effective permissions
 */
export const getUserPermissions = async (
  userId: number | string
): Promise<ApiResponse<Permission[]>> => {
  const config = {
    method: "GET",
    url: `${GET_USER_PERMISSIONS_URL}/${userId}/permissions`,
    headers: authHeader(),
  };

  try {
    const response = await API(config);
    const parsed = JSONParser(response.data, {
      status: "Error",
      data: {} as { permissions?: string[] },
    });
    // Enterprise /admin/users/:id/permissions -> {userId, roleCode, permissions:[codes]}.
    // FE wants Permission[] keyed by permissionKey (role-derived effective perms).
    const codes: string[] = (parsed.data && parsed.data.permissions) || [];
    const data = codes.map((c) => ({ permissionKey: c }));
    return { ...parsed, data } as ApiResponse<Permission[]>;
  } catch (error) {
    console.error("Failed to get user permissions:", error);
    throw error;
  }
};

/**
 * Get a user's permission breakdown (admin-scoped — AddUpdateteam Permissions tab).
 *
 * [UAT-4 Finding B] The enterprise /admin/users/:id/permissions response carries THREE lists —
 * permissions (effective = role ∪ grants), rolePermissions (derived from the member's role) and
 * grantedPermissions (explicit USER_PERMISSION rows). The modal needs the split so role-derived
 * perms render locked (unchecking one would be a lie — the role still grants it) while explicit
 * grants stay editable, and so Save only writes the grant set.
 */
export interface UserPermissionDetail {
  effectiveKeys: string[];
  roleKeys: string[];
  grantedKeys: string[];
  roleCode: string | null;
}

export const getUserPermissionDetail = async (
  userId: number | string
): Promise<UserPermissionDetail> => {
  const config = {
    method: "GET",
    url: `${GET_USER_PERMISSIONS_URL}/${userId}/permissions`,
    headers: authHeader(),
  };
  const response = await API(config);
  const parsed = JSONParser(response.data, {
    status: "Error",
    data: {} as {
      permissions?: string[];
      rolePermissions?: string[];
      grantedPermissions?: string[];
      roleCode?: string | null;
    },
  });
  const d = parsed.data || {};
  return {
    effectiveKeys: Array.isArray(d.permissions) ? d.permissions : [],
    roleKeys: Array.isArray(d.rolePermissions) ? d.rolePermissions : [],
    grantedKeys: Array.isArray(d.grantedPermissions) ? d.grantedPermissions : [],
    roleCode: d.roleCode ?? null,
  };
};

/**
 * Get the CURRENT user's own effective permissions (self-scoped).
 *
 * [G-SELFPERMS-01] PermissionContext must NOT use /admin/users/:id/permissions for the
 * logged-in user — that route is (correctly) guarded by ADMIN_MANAGE_USERS, so every
 * non-admin got 403 and all permission-gated UI collapsed. GET /auth/me is live, requires
 * only a valid JWT, and returns { userPublicId, email, tenantPublicId, roleCode,
 * permissions:[codes] } — the same effective codes the login response carries.
 */
export const getMyPermissions = async (): Promise<ApiResponse<PermissionData>> => {
  const config = {
    method: "GET",
    url: API_V1_BASE + "/auth/me",
    headers: authHeader(),
  };

  try {
    const response = await API(config);
    // /auth/me returns the principal object directly (no {status,data} envelope).
    const raw = JSONParser<{ permissions?: string[] }>(response.data, {});
    const codes: string[] = Array.isArray(raw.permissions) ? raw.permissions : [];
    const data: PermissionData = {
      permissions: codes.map((c) => ({ permissionKey: c })) as PermissionData["permissions"],
      permissionKeys: codes,
    };
    return {
      status: Array.isArray(raw.permissions) ? "Success" : "Error",
      data,
    } as unknown as ApiResponse<PermissionData>;
  } catch (error) {
    console.error("Failed to get own permissions:", error);
    throw error;
  }
};

/**
 * Get user's full permission data
 * [G-SELFPERMS-01] Admin-scoped — only for an ADMIN viewing ANOTHER user's permissions
 * (AddUpdateteam). The current user's own permissions come from getMyPermissions().
 */
export const getUserFullPermissions = async (
  userId: number | string
): Promise<ApiResponse<PermissionData>> => {
  const config = {
    method: "GET",
    url: `${GET_USER_PERMISSIONS_URL}/${userId}/permissions`,
    headers: authHeader(),
  };

  try {
    const response = await API(config);
    const parsed = JSONParser(response.data, {
      status: "Error" as const,
      data: {} as { permissions?: string[] },
    });
    // Adapt {permissions:[codes]} -> {permissions:Permission[], permissionKeys:string[]}.
    const codes: string[] = (parsed.data && parsed.data.permissions) || [];
    const data: PermissionData = {
      permissions: codes.map((c) => ({ permissionKey: c })) as PermissionData["permissions"],
      permissionKeys: codes,
    };
    return { ...parsed, data } as ApiResponse<PermissionData>;
  } catch (error) {
    console.error("Failed to get user full permissions:", error);
    throw error;
  }
};

/**
 * Update user's role
 */
export const updateUserRole = async (
  userId: number | string,
  roleId: number | string,
  updatedBy: number | string
): Promise<ApiResponse<void>> => {
  // Enterprise: PATCH /admin/users/:id/role { roleId } (userId in the path, opaque ULID).
  void updatedBy;
  const config = {
    method: "PATCH",
    url: `${UPDATE_USER_ROLE_URL}/${userId}/role`,
    headers: authHeader(),
    data: { roleId },
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    return JSONParser(decrypted, { status: "Error" });
  } catch (error) {
    console.error("Failed to update user role:", error);
    throw error;
  }
};

/**
 * Update user's permissions (bulk)
 */
export const updateUserPermissions = async (
  userId: number | string,
  permissionKeys: string[],
  updatedBy: number | string
): Promise<ApiResponse<void>> => {
  const config = {
    method: "PUT",
    url: UPDATE_USER_PERMISSIONS_URL,
    headers: authHeader(),
    data: { userId, permissionKeys, updatedBy },
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    return JSONParser(decrypted, { status: "Error" });
  } catch (error) {
    console.error("Failed to update user permissions:", error);
    throw error;
  }
};

/**
 * Get permission sets
 */
export const getPermissionSets = async (): Promise<ApiResponse<PermissionSet[]>> => {
  const config = {
    method: "GET",
    url: GET_PERMISSION_SETS_URL,
    headers: authHeader(),
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    return JSONParser(decrypted, { status: "Error", data: [] });
  } catch (error) {
    console.error("Failed to get permission sets:", error);
    throw error;
  }
};

/**
 * Apply permission set to user
 */
export const applyPermissionSet = async (
  userId: number | string,
  setId: number | string,
  grantedBy: number | string
): Promise<ApiResponse<void>> => {
  const config = {
    method: "POST",
    url: APPLY_PERMISSION_SET_URL,
    headers: authHeader(),
    data: { userId, setId, grantedBy },
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    return JSONParser(decrypted, { status: "Error" });
  } catch (error) {
    console.error("Failed to apply permission set:", error);
    throw error;
  }
};
